import React from "react";
import { PropTypes } from "prop-types";
import cs from "classnames";

// COMPONENTS
import { Carousel } from "@lululemon/ui-library";

// LOCAL COMPONENTS
import CarouselThumbnails from "./components/carousel-thumbnails";
import CarouselChildren from "./components/carousel-children";
import HoverImage from "./components/hover-image";

// CONSTANTS
import { IMAGES_CAROUSEL_REF } from "./utils/constants";

// STYLES
import styles from "./styles/carousel.module.scss";

function ProductMediaCarousel({
  children,
  setRef,
  dataRefresh,
  openZoomModal,
  onSlideClick,
  ModelIsWearingComponent,
  ...carouselProps
}) {
  const {
    activeSlide,
    allColours,
    carouselId,
    carouselRef,
    displayName,
    handleModelIsWearingButton,
    isHorizontalScroll,
    isLastSlide,
    isMobile,
    mediaInfo,
    modelIsWearing,
    modelIsWearingRef,
    onSlideIndexChange,
    pdpCarouselWWMTImageWidths,
    productId,
    productImageAlt,
    renderShopThisLookButton,
    shouldShowModelIsWearing,
    showCarouselArrows,
    showModelIsWearingOverlay,
    swatchData,
    totalSlides
  } = carouselProps;

  const carouselChildren = CarouselChildren({
    ...{
      activeSlide,
      handleModelIsWearingButton,
      isMobile,
      mediaInfo,
      modelIsWearing,
      modelIsWearingRef,
      openZoomModal,
      onSlideClick,
      pdpCarouselWWMTImageWidths,
      productId,
      productImageAlt,
      renderShopThisLookButton,
      showModelIsWearingOverlay,
      shouldShowModelIsWearing,
      totalSlides,
      ModelIsWearingComponent
    }
  });

  const thumbnails = CarouselThumbnails({
    ...{
      activeSlide,
      isMobile,
      shouldShowModelIsWearing,
      mediaInfo,
      productImageAlt
    }
  });

  return (
    <div
      className={styles.imageCarousel}
      ref={(el) => setRef(el, IMAGES_CAROUSEL_REF)}
      id={carouselId}
      data-testid="product-media-carousel"
    >
      {!dataRefresh && (
        <div ref={carouselRef} className={styles.carouselContainer}>
          <HoverImage
            {...{
              displayName,
              isMobile,
              allColours,
              swatchData,
              pdpCarouselWWMTImageWidths
            }}
          />
          <Carousel
            v2
            infinite
            showArrows={showCarouselArrows}
            onSlideIndexChange={(value) => onSlideIndexChange(value)}
            slideIndex={
              activeSlide < carouselChildren?.length
                ? activeSlide
                : carouselChildren.length - 1
            }
            pagination={thumbnails}
            showPagination={mediaInfo?.length > 1}
            classes={{
              carousel: styles.carouselWrapper,
              pagination: !isMobile ? styles.pagination : null,
              wrapper: styles.wrapper
            }}
            itemClass={cs({
              [styles.productImagesSlideOverlay]:
                shouldShowModelIsWearing && isLastSlide
            })}
            sliderClass={styles.slider}
            responsive={{
              any: {
                breakpoint: { max: Number.MAX_SAFE_INTEGER, min: 0 },
                items: 1
              }
            }}
            ssr
            deviceType="any"
            draggable={isHorizontalScroll || isHorizontalScroll === null}
          >
            {carouselChildren}
          </Carousel>
          {children}
        </div>
      )}
    </div>
  );
}

ProductMediaCarousel.propTypes = {
  dataRefresh: PropTypes.bool.isRequired,
  productId: PropTypes.string.isRequired,
  isMobile: PropTypes.bool,
  openZoomModal: PropTypes.func,
  modelIsWearing: PropTypes.arrayOf(PropTypes.shape({})),
  modelIsWearingRef: PropTypes.shape({
    current: PropTypes.shape({})
  }),
  mediaInfo: PropTypes.arrayOf(PropTypes.shape({})),
  setRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object])
};

ProductMediaCarousel.defaultProps = {
  isMobile: false,
  modelIsWearing: false,
  modelIsWearingRef: {
    current: null
  },
  mediaInfo: [],
  openZoomModal: () => {},
  setRef: () => {}
};

export default ProductMediaCarousel;
